import type { Rarity } from "./rarity.js";

export const LOOT_CHANCE_SCALE = 100_000;

export interface LootRarityWeight {
  rarity: Rarity;
  weight: number;
}

export interface MonsterLootEntry {
  dropId: string;
  itemId: string;
  chance: number;
  countMin: number;
  countMax: number;
  rarity?: Rarity;
  rarityRoll?: readonly LootRarityWeight[];
  tier?: number;
  enhancementLevel?: number;
}

export interface MonsterLootTable {
  configId: string;
  items: readonly MonsterLootEntry[];
}

export interface RolledMonsterDrop {
  dropId: string;
  itemId: string;
  quantity: number;
  rarity: Rarity;
  tier: number;
  enhancementLevel: number;
}

export type InclusiveRandomInt = (minimum: number, maximum: number) => number;

function randomInteger(
  randomInt: InclusiveRandomInt,
  minimum: number,
  maximum: number,
): number {
  const result = randomInt(minimum, maximum);
  if (!Number.isInteger(result) || result < minimum || result > maximum)
    throw new RangeError("Random source returned a value outside its range");
  return result;
}

function rollRarity(
  entry: MonsterLootEntry,
  randomInt: InclusiveRandomInt,
): Rarity {
  if (entry.rarity) return entry.rarity;
  if (!entry.rarityRoll?.length) return "common";

  const roll = randomInteger(randomInt, 1, LOOT_CHANCE_SCALE);
  let cumulativeWeight = 0;
  for (const outcome of entry.rarityRoll) {
    cumulativeWeight += outcome.weight;
    if (roll <= cumulativeWeight) return outcome.rarity;
  }
  throw new RangeError("Loot rarity weights do not cover the random range");
}

/**
 * Resolve every entry independently. This function contains no persistence or
 * runtime authority and accepts an injected inclusive random source for tests.
 */
export function rollMonsterLoot(
  table: MonsterLootTable | undefined,
  randomInt: InclusiveRandomInt,
): RolledMonsterDrop[] {
  if (!table) return [];
  const drops: RolledMonsterDrop[] = [];
  for (const entry of table.items) {
    const chanceRoll = randomInteger(randomInt, 1, LOOT_CHANCE_SCALE);
    if (chanceRoll > entry.chance) continue;

    const rarity = rollRarity(entry, randomInt);
    const quantity = randomInteger(randomInt, entry.countMin, entry.countMax);
    drops.push({
      dropId: entry.dropId,
      itemId: entry.itemId,
      quantity,
      rarity,
      tier: entry.tier ?? 1,
      enhancementLevel: entry.enhancementLevel ?? 0,
    });
  }
  return drops;
}
